/**
 * Billing (jobcork-pricing.md): one plan, $29/month, no free tier, no trial.
 *
 * SubscribeScreen is what a signed-in person sees INSTEAD of the product when
 * the API says they may not use it -- the paywall. BillingSection is the
 * Settings card: the plan, when it renews or ends, and Stripe's Customer Portal
 * to change the card or cancel. Both send the person to Stripe's own pages; no
 * card is ever typed into JobCork.
 *
 * Founders see none of this while the founder period is open: the API says
 * they may use the product, and the Settings card says why in one line.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";

export interface Access {
  /** False until Stripe is configured on the server; then nothing here shows. */
  enabled: boolean;
  allowed: boolean;
  reason: "founder" | "subscribed" | "none" | "unknown";
  subscription: { status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.body.errors?.join(" ") ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

async function goToCheckout(): Promise<void> {
  const { url } = await api<{ url: string }>("/api/billing/checkout", { method: "POST", body: "{}" });
  window.location.assign(url);
}

async function goToPortal(): Promise<void> {
  const { url } = await api<{ url: string }>("/api/billing/portal", { method: "POST", body: "{}" });
  window.location.assign(url);
}

/**
 * Read once and clear the `#billing=…` hash Stripe sends the person back
 * with, so a reload does not repeat "confirming" or "cancelled".
 */
export function takeBillingReturn(): "success" | "cancelled" | null {
  const m = /^#billing=(success|cancelled)$/.exec(window.location.hash);
  if (!m) return null;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return m[1] as "success" | "cancelled";
}

/** Stripe's status word, as a person would say it. */
function statusWords(s: { status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean }): string {
  const when = s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  switch (s.status) {
    case "active": return s.cancelAtPeriodEnd ? `Cancelled — access ends ${when ?? "at the end of the period"}` : `Active${when ? ` — renews ${when}` : ""}`;
    case "trialing": return `Trial${when ? ` — first charge ${when}` : ""}`;
    case "past_due": return "Payment past due — update your card to keep access";
    case "canceled": return "Cancelled";
    case "unpaid": return "Unpaid — access is paused";
    case "paused": return "Paused";
    default: return s.status.replace(/_/g, " ");
  }
}

/** The paywall. Shown in place of the product; the person can subscribe or sign out. */
export function SubscribeScreen({ returned, onAllowed }: { returned: "success" | "cancelled" | null; onAllowed: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Back from Checkout: the webhook can land a few seconds after the person
  // does. Ask until it has, rather than showing the paywall to someone who
  // just paid.
  const [confirming, setConfirming] = useState(returned === "success");

  useEffect(() => {
    if (!confirming) return;
    let alive = true;
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const s = await api<Access>("/api/billing/status");
        if (!alive) return;
        if (s.allowed) { onAllowed(); return; }
      } catch { /* keep asking */ }
      if (!alive) return;
      if (tries >= 15) {
        setConfirming(false);
        setError("Your payment went through, but the subscription has not reached JobCork yet. Reload in a moment — if it still shows this, email hello@jobcork.com and it will be put right.");
        return;
      }
      setTimeout(tick, 2000);
    };
    void tick();
    return () => { alive = false; };
  }, [confirming, onAllowed]);

  const subscribe = async () => {
    setBusy(true);
    setError(null);
    try { await goToCheckout(); } catch (e) { setError(describe(e)); setBusy(false); }
  };

  return (
    <section>
      <h1>JobCork is $29 a month</h1>
      <p>
        One plan, everything included: ranked matches with the reasoning shown, tailored resumes and
        letters checked for invented claims, and auto-apply that fills and sends for you. No free
        tier, no trial — pay and use it, or don't. Cancel any time from Settings.
      </p>
      {confirming ? (
        <p className="muted">Confirming your subscription…</p>
      ) : (
        <>
          {returned === "cancelled" && <p className="muted small">Checkout was cancelled — nothing was charged.</p>}
          <p>
            <button className="btn" disabled={busy} onClick={() => void subscribe()}>
              {busy ? "Opening Stripe…" : "Subscribe — $29/month"}
            </button>
          </p>
          <p className="muted small">
            You enter your card on Stripe's page; JobCork never sees it.{" "}
            <a href="/pricing.html">What's included</a> · <a href="/terms.html">Terms</a>
          </p>
        </>
      )}
      {error && <p className="err small">{error}</p>}
    </section>
  );
}

/** The Settings card. Renders nothing at all until billing is switched on. */
export function BillingSection() {
  const [state, setState] = useState<Access | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<Access>("/api/billing/status").then(setState).catch((e) => setError(describe(e)));
  }, []);

  if (!state?.enabled && !error) return null;

  const act = (fn: () => Promise<void>) => async () => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(describe(e)); setBusy(false); }
  };

  const sub = state?.subscription ?? null;

  return (
    <section>
      <h2>Billing</h2>
      {state?.reason === "founder" && (
        <p className="small"><span className="tag ok-tag">Founder member</span> Free during the founder period.</p>
      )}
      {sub && (
        <p className="small"><strong>$29/month.</strong> {statusWords(sub)}</p>
      )}
      {sub ? (
        <p><button className="btn small" disabled={busy} onClick={act(goToPortal)}>{busy ? "Opening Stripe…" : "Manage billing"}</button>
          <span className="muted small"> — change the card, see invoices, or cancel.</span></p>
      ) : state?.reason !== "founder" ? (
        <p>
          <span className="muted small">You're not subscribed. </span>
          <button className="btn small" disabled={busy} onClick={act(goToCheckout)}>{busy ? "Opening Stripe…" : "Subscribe — $29/month"}</button>
        </p>
      ) : null}
      {error && <p className="err small">{error}</p>}
    </section>
  );
}
